import React, { useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon, Building2Icon, DownloadIcon, FileSpreadsheetIcon, MapPinIcon, PhoneIcon, MailIcon, GlobeIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { Company } from '../../types';
import { exportCompaniesToExcel } from '../../utils/excelExport';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../ui/Pagination';
import { FormSection } from '../ui/FormSection';
export function Companies() {
  const {
    companies,
    addCompany,
    updateCompany,
    deleteCompany
  } = useData();
  const {
    user
  } = useAuth();
  const isAdmin = user?.role === 'administrador';
  const {
    canCreate,
    canEdit,
    canDelete,
    isReadOnly
  } = usePermissions();
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    size: '',
    location: '',
    phone: '',
    email: '',
    website: '',
    notes: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const filteredCompanies = companies.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()) || c.industry.toLowerCase().includes(searchTerm.toLowerCase()));
  const {
    paginatedItems: paginatedCompanies,
    currentPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
    goToPage,
    nextPage,
    prevPage
  } = usePagination(filteredCompanies);
  const openNewForm = () => {
    setSelectedCompany(null);
    setFormData({
      name: '',
      industry: '',
      size: '',
      location: '',
      phone: '',
      email: '',
      website: '',
      notes: ''
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openEditForm = (company: Company) => {
    setSelectedCompany(company);
    setFormData({
      name: company.name,
      industry: company.industry,
      size: company.size,
      location: company.location,
      phone: company.phone,
      email: company.email,
      website: company.website || '',
      notes: company.notes
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openDeleteConfirm = (company: Company) => {
    setSelectedCompany(company);
    setIsDeleteConfirmOpen(true);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!formData.name) errors.name = 'El nombre es requerido';
    if (!formData.industry) errors.industry = 'La industria es requerida';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    if (selectedCompany) {
      updateCompany(selectedCompany.id, formData);
    } else {
      addCompany(formData);
    }
    setIsFormOpen(false);
  };
  const handleDelete = () => {
    if (selectedCompany) {
      deleteCompany(selectedCompany.id);
      setIsDeleteConfirmOpen(false);
    }
  };
  const handleExportToExcel = async () => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    exportCompaniesToExcel(companies);
    setIsExporting(false);
  };
  return <div className="space-y-6" data-id="element-568">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-569">
        <div data-id="element-570">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-571">
            Empresas
          </h1>
          <p className="text-gray-500 dark:text-gray-400" data-id="element-572">
            Gestión de empresas y organizaciones
          </p>
        </div>
        {canCreate('companies') && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-574" />} onClick={openNewForm} data-id="element-573">
            Nueva Empresa
          </Button>}
      </div>

      {/* Export Card */}
      {isAdmin && <Card className="bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-950/30 dark:to-blue-950/30 border-indigo-200 dark:border-indigo-800" data-id="element-575">
          <div className="flex items-center justify-between" data-id="element-576">
            <div className="flex items-center gap-4" data-id="element-577">
              <div className="w-14 h-14 rounded-xl bg-blue-500 dark:bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/20" data-id="element-578">
                <FileSpreadsheetIcon className="w-7 h-7 text-white" data-id="element-579" />
              </div>
              <div data-id="element-580">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1" data-id="element-581">
                  Exportar Base de Datos
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-582">
                  Descarga todas las empresas en formato Excel
                </p>
                <div className="flex items-center gap-4 mt-2" data-id="element-583">
                  <div className="flex items-center gap-1.5 text-sm text-blue-700 dark:text-blue-400" data-id="element-584">
                    <Building2Icon className="w-4 h-4" data-id="element-585" />
                    <span className="font-medium" data-id="element-586">
                      {companies.length} empresas
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <Button leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-588" />} onClick={handleExportToExcel} isLoading={isExporting} className="shadow-lg shadow-blue-500/20 hover:shadow-xl hover:shadow-blue-500/30 transition-all duration-200" data-id="element-587">
              {isExporting ? 'Exportando...' : 'Descargar Excel'}
            </Button>
          </div>
        </Card>}

      {/* Filters */}
      <Card data-id="element-589">
        <div className="relative" data-id="element-590">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-591" />
          <Input placeholder="Buscar empresas por nombre o industria..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-id="element-592" />
        </div>
      </Card>

      {/* Companies List */}
      <Card padding="none" data-id="element-593">
        <div className="overflow-x-auto" data-id="element-594">
          <table className="w-full" data-id="element-595">
            <thead data-id="element-596">
              <tr className="border-b border-gray-200 dark:border-gray-700" data-id="element-597">
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-598">
                  Empresa
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-599">
                  Contacto
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-600">
                  Ubicación
                </th>
                <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-601">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-602">
              {filteredCompanies.length === 0 ? <tr data-id="element-603">
                  <td colSpan={4} className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-604">
                    No se encontraron empresas
                  </td>
                </tr> : paginatedCompanies.map(company => <tr key={company.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50" data-id="element-605">
                    <td className="py-4 px-6" data-id="element-606">
                      <div className="flex items-center gap-3" data-id="element-607">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400" data-id="element-608">
                          <Building2Icon className="w-5 h-5" data-id="element-609" />
                        </div>
                        <div data-id="element-610">
                          <p className="font-medium text-gray-900 dark:text-white" data-id="element-611">
                            {company.name}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-612">
                            {company.industry} • {company.size}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6" data-id="element-613">
                      <div className="space-y-1" data-id="element-614">
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300" data-id="element-615">
                          <PhoneIcon className="w-3.5 h-3.5" data-id="element-616" />
                          {company.phone}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300" data-id="element-617">
                          <MailIcon className="w-3.5 h-3.5" data-id="element-618" />
                          {company.email}
                        </div>
                        {company.website && <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400" data-id="element-619">
                            <GlobeIcon className="w-3.5 h-3.5" data-id="element-620" />
                            <a href={`https://${company.website}`} target="_blank" rel="noopener noreferrer" className="hover:underline" data-id="element-621">
                              {company.website}
                            </a>
                          </div>}
                      </div>
                    </td>
                    <td className="py-4 px-6" data-id="element-622">
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300" data-id="element-623">
                        <MapPinIcon className="w-4 h-4" data-id="element-624" />
                        {company.location}
                      </div>
                    </td>
                    <td className="py-4 px-6" data-id="element-625">
                      <div className="flex items-center justify-end gap-2" data-id="element-626">
                        {canEdit('companies') && <button onClick={() => openEditForm(company)} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" data-id="element-627">
                            <EditIcon className="w-4 h-4" data-id="element-628" />
                          </button>}
                        {canDelete('companies') && <button onClick={() => openDeleteConfirm(company)} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" data-id="element-629">
                            <TrashIcon className="w-4 h-4" data-id="element-630" />
                          </button>}
                      </div>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="px-6 pb-4" data-id="element-631">
          <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} startIndex={startIndex} endIndex={endIndex} onPageChange={goToPage} onNext={nextPage} onPrev={prevPage} data-id="element-632" />
        </div>
      </Card>

      {/* Form Modal */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={selectedCompany ? 'Editar Empresa' : 'Nueva Empresa'} size="lg" icon={<Building2Icon className="w-6 h-6" data-id="element-634" />} accentColor="purple" data-id="element-633">
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-635">
          <FormSection title="Información de la Empresa" data-id="element-636">
            <div className="space-y-4" data-id="element-637">
              <Input label="Nombre de la Empresa" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} error={formErrors.name} placeholder="Ej: Tech Solutions S.A." data-id="element-638" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-639">
                <Input label="Industria" value={formData.industry} onChange={e => setFormData({
                ...formData,
                industry: e.target.value
              })} error={formErrors.industry} placeholder="Ej: Tecnología" data-id="element-640" />
                <Input label="Tamaño (Empleados)" value={formData.size} onChange={e => setFormData({
                ...formData,
                size: e.target.value
              })} placeholder="Ej: 50-100" data-id="element-641" />
              </div>
            </div>
          </FormSection>

          <FormSection title="Contacto" data-id="element-642">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-643">
              <Input label="Teléfono" value={formData.phone} onChange={e => setFormData({
              ...formData,
              phone: e.target.value
            })} placeholder="+1 (555) 000-0000" data-id="element-644" />
              <Input label="Email" type="email" value={formData.email} onChange={e => setFormData({
              ...formData,
              email: e.target.value
            })} placeholder="contacto@empresa.com" data-id="element-645" />
            </div>
          </FormSection>

          <FormSection title="Ubicación y Web" data-id="element-646">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-647">
              <Input label="Ubicación" value={formData.location} onChange={e => setFormData({
              ...formData,
              location: e.target.value
            })} placeholder="Ciudad, País" data-id="element-648" />
              <Input label="Sitio Web" value={formData.website} onChange={e => setFormData({
              ...formData,
              website: e.target.value
            })} placeholder="www.ejemplo.com" data-id="element-649" />
            </div>
          </FormSection>

          <FormSection title="Notas" data-id="element-650">
            <Textarea label="Notas Adicionales" value={formData.notes} onChange={e => setFormData({
            ...formData,
            notes: e.target.value
          })} rows={3} placeholder="Información adicional sobre la empresa..." data-id="element-651" />
          </FormSection>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-800" data-id="element-652">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-653">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-654">
              {selectedCompany ? 'Guardar Cambios' : 'Crear Empresa'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <Modal isOpen={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)} title="Confirmar Eliminación" size="sm" data-id="element-655">
        <div className="space-y-4" data-id="element-656">
          <p className="text-gray-600 dark:text-gray-400" data-id="element-657">
            ¿Está seguro de que desea eliminar la empresa "
            {selectedCompany?.name}"?
          </p>
          <div className="flex justify-end gap-3" data-id="element-658">
            <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)} data-id="element-659">
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete} data-id="element-660">
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}